import React, { useState, useEffect } from 'react';
import { Folder, FolderPlus, Trash2, StickyNote, Share2, X, MoreVertical, Pencil, Check } from 'lucide-react';

const FOLDER_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1'];

export default function NoteFoldersSidebar({ folders = [], selectedFolder, onSelectFolder, onCreateFolder, onDeleteFolder, onUpdateFolder, activeTab, onTabChange, sharedCount = 0, theme }) {
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#3B82F6');

  // Per-folder kebab menu + inline edit (rename / recolor) state.
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#3B82F6');

  // Close the open kebab menu on any outside click.
  useEffect(() => {
    if (!menuOpenId) return;
    const close = () => setMenuOpenId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpenId]);

  const handleCreate = () => {
    if (!newFolderName.trim()) return;
    onCreateFolder(newFolderName.trim(), newFolderColor);
    setNewFolderName('');
    setShowNewFolder(false);
  };

  const startEdit = (folder) => {
    setEditingId(folder.id);
    setEditName(folder.name);
    setEditColor(folder.color || '#3B82F6');
    setMenuOpenId(null);
  };

  const handleSaveEdit = () => {
    if (!editName.trim()) return;
    onUpdateFolder?.(editingId, { name: editName.trim(), color: editColor });
    setEditingId(null);
  };

  return (
    <div className={`w-full h-full font-rasa ${theme === 'dark' ? 'bg-gray-850' : 'bg-gray-100'} flex flex-col`}>
      <div className="p-3 space-y-1 flex-shrink-0">
        {/* My Notes */}
        <button
          onClick={() => { onSelectFolder(null); onTabChange('my_notes'); }}
          className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-rasa transition-colors ${
            activeTab === 'my_notes' && !selectedFolder
              ? theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-white text-blue-600 shadow-sm'
              : theme === 'dark' ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white'
          }`}
        >
          <StickyNote size={16} />
          All Notes
        </button>

        {/* Shared with Me */}
        <button
          onClick={() => { onSelectFolder(null); onTabChange('shared'); }}
          className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-rasa transition-colors ${
            activeTab === 'shared'
              ? theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-white text-blue-600 shadow-sm'
              : theme === 'dark' ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white'
          }`}
        >
          <div className="flex items-center gap-2">
            <Share2 size={16} />
            Shared with Me
          </div>
          {sharedCount > 0 && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}>
              {sharedCount}
            </span>
          )}
        </button>

        {/* Unfiled */}
        <button
          onClick={() => { onSelectFolder('unfiled'); onTabChange('my_notes'); }}
          className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-rasa transition-colors ${
            selectedFolder === 'unfiled'
              ? theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-white text-blue-600 shadow-sm'
              : theme === 'dark' ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white'
          }`}
        >
          <Folder size={16} />
          Unfiled
        </button>
      </div>

      {/* Divider */}
      <div className={`mx-3 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`} />

      {/* Folders */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-rasa font-medium uppercase tracking-wider ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
            Folders
          </span>
          <button
            onClick={() => setShowNewFolder(true)}
            className={`p-1 rounded transition-colors ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
            title="New folder"
          >
            <FolderPlus size={14} />
          </button>
        </div>

        {showNewFolder && (
          <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} space-y-2`}>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className={`w-full px-2 py-1 text-sm font-rasa rounded border outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <div className="flex items-center gap-1">
              {FOLDER_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setNewFolderColor(c)}
                  className={`w-5 h-5 rounded-full border-2 ${newFolderColor === c ? 'border-white shadow' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div className="flex gap-1">
              <button onClick={handleCreate} className="flex-1 px-2 py-1 bg-blue-600 text-white text-xs font-rasa rounded hover:bg-blue-700">
                Create
              </button>
              <button onClick={() => setShowNewFolder(false)} className="px-2 py-1 text-xs rounded hover:bg-gray-200 dark:hover:bg-gray-700">
                <X size={12} />
              </button>
            </div>
          </div>
        )}

        {folders.filter(Boolean).map(folder => (
          editingId === folder.id ? (
            <div
              key={folder.id}
              className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} space-y-2`}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Folder name"
                className={`w-full px-2 py-1 text-sm font-rasa rounded border outline-none ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveEdit();
                  if (e.key === 'Escape') setEditingId(null);
                }}
              />
              <div className="flex items-center gap-1">
                {FOLDER_COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setEditColor(c)}
                    className={`w-5 h-5 rounded-full border-2 ${editColor === c ? 'border-white shadow' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
              <div className="flex gap-1">
                <button onClick={handleSaveEdit} className="flex-1 px-2 py-1 bg-blue-600 text-white text-xs font-rasa rounded hover:bg-blue-700 flex items-center justify-center gap-1">
                  <Check size={12} /> Save
                </button>
                <button onClick={() => setEditingId(null)} className="px-2 py-1 text-xs rounded hover:bg-gray-200 dark:hover:bg-gray-700">
                  <X size={12} />
                </button>
              </div>
            </div>
          ) : (
            <div
              key={folder.id}
              className={`group flex items-center justify-between px-3 py-2 rounded-lg text-sm font-rasa cursor-pointer transition-colors ${
                selectedFolder === folder.id
                  ? theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-white text-blue-600 shadow-sm'
                  : theme === 'dark' ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white'
              }`}
              onClick={() => { onSelectFolder(folder.id); onTabChange('my_notes'); }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: folder.color }} />
                <span className="truncate">{folder.name}</span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <span className={`text-[10px] ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                  {folder.notes_count}
                </span>
                <div className="relative">
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === folder.id ? null : folder.id); }}
                    className={`p-0.5 rounded transition-colors ${theme === 'dark' ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-500 hover:bg-gray-200'}`}
                    title="Folder actions"
                  >
                    <MoreVertical size={14} />
                  </button>

                  {menuOpenId === folder.id && (
                    <div
                      className={`absolute right-0 top-full mt-1 z-20 w-32 rounded-lg border shadow-lg py-1 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => startEdit(folder)}
                        className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs font-rasa ${theme === 'dark' ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        onClick={() => { setMenuOpenId(null); onDeleteFolder(folder.id); }}
                        className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs font-rasa text-red-500 ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}
